import { eachDayOfInterval, format } from 'date-fns';

import i18n from '@/libs/i18n';

// Labels map for laterality and label
const LABELS: Record<string, Record<string, string>> = {
  OD: {
    MACULA: 'Macula Centered', // OD (Right)
    OPTIC: 'Optic Disc Centered', // OD (Right)
  },
  OS: {
    MACULA: 'Macula Centered', // OS (Left)
    OPTIC: 'Optic Disc Centered', // OS (Left)
  },
};

const EYESIDE: Record<string, string> = {
  OD: 'right',
  OS: 'left',
};

/**
 * This function extracts the label and eye side from an image name.
 * The expected format is: `20241017104838_OD_MACULA.jpg` or `20241017104902_OD_OPTIC.jpg`
 * where OD means the right eye and OS means the left eye, and MACULA and OPTIC are labels.
 * Returns an empty object if the format is incorrect.
 */
export const getLateralityFromImageName = (imageName: string): Record<string, string> => {
  const parts = imageName.split('_');

  // Ensure the image name has at least 3 parts (timestamp, eyeside, label)
  if (parts.length < 3) return {};

  const eyesideKey = parts[1] as 'OD' | 'OS';
  const labelKey = parts[2].split('.')[0].toUpperCase() as 'MACULA' | 'OPTIC';

  // Validate keys
  if (!LABELS[eyesideKey]?.[labelKey]) return {};

  const label = LABELS[eyesideKey][labelKey];
  const eyeside = EYESIDE[eyesideKey];

  return { label, eyeside };
};

// Dummy data for demonstration
export const generateDummyData = (start: Date, end: Date): { date: string; cases: number }[] => {
  return eachDayOfInterval({ start, end }).map((date) => ({
    date: format(date, 'yyyy-MM-dd'),
    cases: Math.floor(Math.random() * 100) + 50,
  }));
};

export const getPatientInfoFromPatientId = (patientId: string) => {
  const match = /(.+?)_(\d{2}\/\d{2}\/\d{4})$/.exec(patientId);
  if (!match)
    return {
      patientName: '',
      birthdate: '',
    };

  const patientName = match[1];
  const birthdate = match[2];
  const birthdateParts = birthdate.split('/');
  const birthdateFormatted = `${birthdateParts[2]}-${birthdateParts[1]}-${birthdateParts[0]}`;
  const age = new Date().getFullYear() - parseInt(birthdateParts[2], 10);

  return {
    patientName,
    birthdate: birthdateFormatted,
    age,
  };
};

export const CASE_STATUS = {
  NOT_SUBMITTED: -4,
  NOT_STARTED: -3,
  PENDING: -2,
  INSUFFICIENT_QUALITY: -1,
  SEE_DOCTOR_ASAP: 0,
  RETURN_6_MO: 6,
  RETURN_12_MO: 12,
  IN_PROGRESS: 77,
  DATA_NOT_PROCESSED: 88,
  NO_DATA_FOUND: 99,
};

export const CASE_STATUS_TEXT = {
  [CASE_STATUS.NOT_SUBMITTED]: i18n.t('case-sts-not-submitted'),
  [CASE_STATUS.NOT_STARTED]: i18n.t('case-sts-not-started'),
  [CASE_STATUS.PENDING]: i18n.t('case-sts-pending'),
  [CASE_STATUS.INSUFFICIENT_QUALITY]: i18n.t('case-sts-insufficient-quality'),
  [CASE_STATUS.SEE_DOCTOR_ASAP]: i18n.t('case-sts-see-doctor-asap'),
  [CASE_STATUS.RETURN_6_MO]: i18n.t('case-sts-return-6-mo'),
  [CASE_STATUS.RETURN_12_MO]: i18n.t('case-sts-return-12-mo'),
  // [CASE_STATUS.COMPLETE]: i18n.t('case-sts-complete'),
  [CASE_STATUS.IN_PROGRESS]: i18n.t('case-sts-in-progress'),
  [CASE_STATUS.DATA_NOT_PROCESSED]: i18n.t('case-sts-data-not-processed'),
  [CASE_STATUS.NO_DATA_FOUND]: i18n.t('case-sts-no-data-found'),
};

export function extractBaseImageName(path: string): string {
  const start = path.lastIndexOf('/') + 1;
  const end = path.lastIndexOf('.');

  // if the dot comes before the slash (or isn’t there), just grab everything after the slash
  if (end < start) return path.substring(start);

  return path.substring(start, end);
}

export const isCaseSubmitted = (resultCode: number | null): boolean => {
  if (resultCode === null) return false;
  return resultCode !== CASE_STATUS.NOT_SUBMITTED && resultCode !== CASE_STATUS.NOT_STARTED;
};
